import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import PageHeader from "../../components/admin/PageHeader";
import InfoBanner from "../../components/admin/InfoBanner";
import FilterBar from "../../components/admin/FilterBar";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;
const emptyForm = { image: null, imagePreview: "", name: "", text: "" };

const compressSquare = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const size = 400;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        const s = Math.min(img.width, img.height);
        const ox = (img.width - s) / 2,
          oy = (img.height - s) / 2;
        ctx.drawImage(img, ox, oy, s, s, 0, 0, size, size);
        canvas.toBlob(
          (blob) =>
            resolve(
              new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              }),
            ),
          "image/jpeg",
          0.9,
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

export const TestimonialCrud = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 12,
    total: 0,
    total_pages: 1,
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async (page = 1) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${BASE_URI}/entities?category=testimonial&page=${page}&limit=${pagination.page_size}`,
        getAuthHeaders(),
      );
      if (res.data?.data) {
        setTestimonials(
          res.data.data.map((i) => ({
            id: i.id,
            image: i.data.images?.[0] || "",
            name: i.data.name || "",
            text: i.data.text || "",
          })),
        );
        setPagination({
          page: res.data.page,
          page_size: res.data.limit,
          total: res.data.total,
          total_pages: Math.ceil(res.data.total / res.data.limit),
        });
      }
      setError("");
    } catch {
      setError("Failed to fetch testimonials");
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setForm(emptyForm);
    setEditingTestimonial(null);
    setDialogOpen(true);
  };
  const openEdit = (t) => {
    setForm({ image: null, imagePreview: t.image, name: t.name, text: t.text });
    setEditingTestimonial(t);
    setDialogOpen(true);
  };

  const handleImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    const compressed = await compressSquare(file);
    setForm((f) => ({
      ...f,
      image: compressed,
      imagePreview: URL.createObjectURL(compressed),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.warning("Please fill all required fields");
      return;
    }
    try {
      setLoading(true);
      const fd = new FormData();
      if (form.image) fd.append("images", form.image);
      fd.append("name", form.name);
      fd.append("text", form.text);
      const hdrs = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      };
      if (editingTestimonial) {
        await axios.put(
          `${BASE_URI}/entities/${editingTestimonial.id}?category=testimonial`,
          fd,
          hdrs,
        );
        toast.success("Testimonial updated!");
      } else {
        await axios.post(`${BASE_URI}/entities?category=testimonial`, fd, hdrs);
        toast.success("Testimonial created!");
      }
      setDialogOpen(false);
      fetchTestimonials(pagination.page);
    } catch {
      toast.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const deleteTestimonial = async () => {
    try {
      setDeleteLoading(true);
      await axios.delete(
        `${BASE_URI}/entities/${deleteId}?category=testimonial`,
        getAuthHeaders(),
      );
      toast.success("Deleted successfully!");
      setDeleteId(null);
      fetchTestimonials(
        testimonials.length === 1 && pagination.page > 1
          ? pagination.page - 1
          : pagination.page,
      );
    } catch {
      toast.error("Failed to delete.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const filtered = testimonials.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Testimonials Management"
        description="Manage student testimonials displayed on the homepage"
        action={
          <Button onClick={openAdd}>
            <Plus className="h-4 w-4" /> Add Testimonial
          </Button>
        }
      />
      <InfoBanner
        title="Testimonials Management"
        description="Showcase student success stories. Photos are auto-cropped to a square for a consistent look."
        capabilities={[
          "Square photo auto-crop",
          "Student name & quote",
          "12 per page pagination",
        ]}
      />
      <FilterBar
        search={search}
        onSearch={setSearch}
        placeholder="Search by name…"
        count={filtered.length}
      />
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Photo</TableHead>
              <TableHead className="w-[160px]">Name</TableHead>
              <TableHead>Testimonial</TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center py-12 text-gray-400"
                >
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
                    Loading…
                  </div>
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center py-12 text-gray-400"
                >
                  No testimonials yet.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>
                    {t.image ? (
                      <img
                        src={t.image}
                        alt={t.name}
                        className="h-12 w-12 rounded-full object-cover border border-gray-200"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                        {t.name?.[0] || "?"}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium text-gray-900">
                    {t.name}
                  </TableCell>
                  <TableCell className="text-gray-600 text-sm italic max-w-[380px]">
                    <p className="line-clamp-3">"{t.text}"</p>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEdit(t)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setDeleteId(t.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-1 p-4 border-t border-gray-100">
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchTestimonials(pagination.page - 1)}
              disabled={pagination.page === 1 || loading}
            >
              Previous
            </Button>
            {Array.from(
              { length: pagination.total_pages },
              (_, i) => i + 1,
            ).map((p) => (
              <Button
                key={p}
                size="sm"
                variant={p === pagination.page ? "default" : "outline"}
                onClick={() => fetchTestimonials(p)}
                disabled={loading}
              >
                {p}
              </Button>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchTestimonials(pagination.page + 1)}
              disabled={pagination.page === pagination.total_pages || loading}
            >
              Next
            </Button>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete testimonial?"
        description="This action cannot be undone."
        onConfirm={deleteTestimonial}
        onCancel={() => setDeleteId(null)}
        loading={deleteLoading}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingTestimonial ? "Edit Testimonial" : "Add Testimonial"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
            <div className="space-y-1.5">
              <Label>Student Photo</Label>
              <div className="flex items-center gap-3">
                {form.imagePreview && (
                  <img
                    src={form.imagePreview}
                    alt=""
                    className="h-14 w-14 rounded-full object-cover border border-gray-200"
                  />
                )}
                <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                  <Upload className="h-4 w-4" />{" "}
                  {form.imagePreview ? "Change" : "Upload"} photo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImage}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>
                Student Name <span className="text-red-500">*</span>
              </Label>
              <Input
                placeholder="Full name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                Testimonial <span className="text-red-500">*</span>
              </Label>
              <Textarea
                placeholder="Student's feedback…"
                rows={4}
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                required
              />
            </div>
          </form>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving…" : editingTestimonial ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestimonialCrud;
